import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { RouterOutputs } from "@/lib/trpc";

/**
 * YOUR WEEK — Stage B: the owner-invoked private AI reflection card.
 *
 * THE ONLY MUTATION-BEARING SURFACE IN YOUR WEEK, and deliberately the last
 * section: everything above it is the owner's own declared and derived weekly
 * material, and this card is subordinate to it (design record §18; the client
 * boundary contract permits exactly one `useMutation` here and nowhere else).
 *
 * WHAT IT OWNS: one mutation hook, the ephemeral result, the disclosure, and
 * the rendering of every result variant the server defines.
 *
 * WHAT IT DOES NOT OWN: queries, navigation, persistence, receipt writes and
 * any disclosure control. Nothing here writes anything, on the client or the
 * server. Generation changes no server state, so nothing is invalidated and
 * nothing is refetched afterwards.
 *
 * ── STATE MODEL, and why it is shaped this way ────────────────────────────
 *
 *   view      the committed outcome: a server result, or a failure, or neither
 *   inFlight  whether a request this card started is still running (for render)
 *   busy      the SAME fact as a ref, for the synchronous re-entry guard
 *   seq       a monotonic id for the latest invocation
 *   scope     a ref holding the CURRENT week and workspace
 *
 * Three pieces of state and two refs, no persistence and no cache. Everything
 * else is derived.
 *
 * A COMPLETION IS COMMITTED ONLY IF IT STILL BELONGS HERE. Every callback is
 * gated on both the sequence id and the scope the request was issued for, so a
 * response that arrives after the owner moved to another week or workspace, or
 * after a newer request overtook it, is discarded instead of rendered.
 *
 * THE FIRST VERSION OF THIS COMPONENT GOT THAT WRONG, and the way it was wrong
 * is worth keeping written down. It cleared the result in an effect and called
 * `generate.reset()`. But `MutationObserver.reset()` only detaches the
 * observer: it does NOT cancel the running mutation, and callbacks passed to
 * `useMutation` live in the mutation's OPTIONS, invoked directly by
 * `Mutation.execute`. So the reset was a no-op against a pending request, and a
 * week-A reflection rendered under week B. Guarding at the callback, on data
 * the callback itself receives, is what actually holds. `YourWeekReflectionCard.race.test.tsx`
 * pins it against the real react-query machinery, because a hand-written stub
 * cannot express settle ordering at all and reported this defect as green.
 *
 * OF THE TWO GATES, THE SCOPE CHECK IS THE LOAD-BEARING ONE. The sequence check
 * is deliberate redundancy, and its necessity is NOT demonstrable against the
 * current library: `MutationObserver.mutate` detaches the previous mutation, so
 * a superseded request's per-call callbacks never fire at all, and removing
 * `mine === seq.current` leaves every test green. It is kept because that
 * detach is a library-internal behaviour this component would otherwise depend
 * on silently — moving these callbacks up to the hook's own options, a very
 * ordinary refactor, would make the sequence check load-bearing immediately.
 *
 * EPHEMERAL BY CONSTRUCTION. The outcome lives in one `useState` and nowhere
 * else: no storage, no URL state, no query cache, no draft. A reload clears it
 * because there is nothing to reload from.
 *
 * DISCLOSURE IS NOT A DIALOG. It is visible beside the action BEFORE any click
 * (design record §9); clicking is the consent for that one invocation, and
 * nothing is recorded about the consent. It stays visible after a result, so
 * the reader can still see how the text was produced.
 */

type Result = RouterOutputs["yourWeekReflection"]["generate"];
type Scope = { weekStartKey: string; workspaceTeamId: number | null };

const sameScope = (a: Scope, b: Scope) =>
  a.weekStartKey === b.weekStartKey && a.workspaceTeamId === b.workspaceTeamId;

export function YourWeekReflectionCard({
  weekStartKey,
  workspaceTeamId,
  isAr,
}: {
  weekStartKey: string;
  workspaceTeamId: number | null;
  isAr: boolean;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);

  const [view, setView] = useState<{ result: Result | null; failed: boolean }>({
    result: null,
    failed: false,
  });
  const [inFlight, setInFlight] = useState(false);
  // WHICH scope the running request belongs to. Without this the card kept
  // saying "Writing your reflection…" after the owner switched week, claiming a
  // reflection was being written for a week nothing had been requested for.
  const [inFlightAt, setInFlightAt] = useState<Scope | null>(null);

  // The same fact as `inFlight`, kept as a ref because the state value is
  // STALE WITHIN AN EVENT BATCH. Two clicks dispatched before React re-renders
  // both read `inFlight === false` from their own render's closure, so a
  // state-based guard would let both through and make two provider calls. A
  // ref is written and read in the same tick, so the second click sees it.
  const busy = useRef(false);

  const seq = useRef(0);
  const scope = useRef<Scope>({ weekStartKey, workspaceTeamId });
  scope.current = { weekStartKey, workspaceTeamId };

  const generate = trpc.yourWeekReflection.generate.useMutation();

  // Changing the week or the workspace drops whatever is on screen, WITHOUT
  // generating anything for the new context. A request already in flight is
  // left to finish and is discarded when it lands, by the guard in `start`.
  useEffect(() => {
    setView({ result: null, failed: false });
  }, [weekStartKey, workspaceTeamId]);

  const start = () => {
    // A handler-level guard, so the invariant does not depend on the disabled
    // attribute being rendered before a second event arrives.
    if (busy.current) return;
    busy.current = true;
    const mine = ++seq.current;
    const at: Scope = { weekStartKey, workspaceTeamId };
    const stillOurs = () => mine === seq.current && sameScope(at, scope.current);

    setInFlight(true);
    setInFlightAt(at);
    generate.mutate(at, {
      onSuccess: r => { if (stillOurs()) setView({ result: r, failed: false }); },
      onError: () => { if (stillOurs()) setView({ result: null, failed: true }); },
      onSettled: () => {
        if (mine === seq.current) { busy.current = false; setInFlight(false); setInFlightAt(null); }
      },
    });
  };

  const result = view.result;
  const inProgress = result?.kind === "in_progress";

  // A request started for THIS week and workspace, versus one still finishing
  // for a scope the owner has since left. Both keep the action disabled — the
  // provider call is genuinely running and `busy` would refuse a second one —
  // but only the first may claim a reflection is being written for what is on
  // screen. Nothing is cancelled: the completion is discarded when it lands.
  const here: Scope = { weekStartKey, workspaceTeamId };
  const pendingHere = inFlight && inFlightAt !== null && sameScope(inFlightAt, here);
  const pendingElsewhere = inFlight && !pendingHere;

  /** Coarse only, and a whole sentence: never the limiter's real numbers. */
  const waitSentence = (ms: number): string => {
    const minutes = Math.ceil(Math.max(0, ms) / 60_000);
    if (minutes > 60) {
      return t(
        "You've reflected several times today. Try again later today.",
        "لقد تأمّلت عدة مرات اليوم. حاول مجدداً لاحقاً اليوم.",
      );
    }
    if (minutes <= 1) {
      return t(
        "You've reflected a few times just now. Try again in a minute.",
        "لقد تأمّلت عدة مرات للتو. حاول مجدداً بعد دقيقة.",
      );
    }
    const arabicMinutes =
      minutes === 2 ? "دقيقتين" : minutes <= 10 ? `${minutes} دقائق` : `${minutes} دقيقة`;
    return t(
      `You've reflected a few times just now. Try again in about ${minutes} minutes.`,
      `لقد تأمّلت عدة مرات للتو. حاول مجدداً بعد نحو ${arabicMinutes}.`,
    );
  };

  const disclosure = (
    <p className="text-xs leading-relaxed text-muted-foreground" data-testid="yourweek-reflection-disclosure">
      {t(
        "Reflecting sends this week's private context, including unapproved drafts, to OpenAI to write your reflection. Receipt does not store the reflection. Visible only to you.",
        "يُرسل التأمّل سياق أسبوعك الخاص، بما في ذلك المسودات غير المعتمدة، إلى OpenAI لكتابة تأمّلك. لا يخزّن Receipt هذا التأمّل. مرئي لك وحدك.",
      )}
    </p>
  );

  const action = (
    <button
      type="button"
      data-testid="yourweek-reflection-action"
      disabled={inFlight}
      onClick={start}
      className="inline-flex items-center gap-2 rounded-full border border-border/60 px-3 py-1.5 text-xs hover:bg-accent/50 disabled:opacity-50"
    >
      {inFlight ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      {pendingHere
        ? t("Writing your reflection…", "يُكتب تأمّلك…")
        : pendingElsewhere
          ? t("Finishing an earlier request…", "جارٍ إنهاء طلب سابق…")
          : t("Reflect on your week with AI", "تأمّل أسبوعك بالذكاء الاصطناعي")}
    </button>
  );

  // A transport failure and a server `unavailable` are the same thing to the
  // reader: the reflection did not arrive, and Your Week above is untouched.
  // Nothing technical is rendered: no status, no code, no reason tag.
  const unavailable = result?.kind === "unavailable";
  const reason = result?.kind === "unavailable" ? result.reason : null;
  // §18: a retry action only where the server said the failure was transient.
  const mayRetry = view.failed || reason === "timeout" || reason === "transport";

  const body = () => {
    if (pendingHere) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-pending">
          {t("Writing your reflection…", "يُكتب تأمّلك…")}
        </p>
      );
    }

    // A request from a week or workspace the owner has left is still running.
    // Say so plainly rather than attributing it to what is on screen, and start
    // nothing for the new scope — that stays the owner's decision.
    if (pendingElsewhere) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-pending-elsewhere">
          {t(
            "An earlier request is still finishing. Nothing is being written for this week.",
            "لا يزال طلب سابق قيد الإنهاء. ولا يُكتب شيء لهذا الأسبوع.",
          )}
        </p>
      );
    }

    // The server already had a reflection running for this week when we asked.
    // That is a wait, not a failure, and it must stay RECOVERABLE: the action
    // below is enabled so the owner can ask again when they choose. Nothing
    // here polls or retries on its own.
    if (inProgress) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-in-progress">
          {t(
            "A reflection for this week is already being written. Try again in a moment.",
            "يجري بالفعل كتابة تأمّل لهذا الأسبوع. حاول مجدداً بعد لحظات.",
          )}
        </p>
      );
    }

    if (view.failed || unavailable) {
      return (
        <div className="space-y-2" data-testid="yourweek-reflection-unavailable">
          <p className="text-sm text-muted-foreground">
            {t(
              "The reflection isn't available right now. Your Week above is unaffected.",
              "التأمّل غير متاح الآن. أسبوعك أعلاه لم يتأثر.",
            )}
          </p>
          {mayRetry ? (
            <button
              type="button"
              data-testid="yourweek-reflection-retry"
              disabled={inFlight}
              onClick={start}
              className="inline-flex rounded-full border border-border/60 px-3 py-1 text-xs hover:bg-accent/50 disabled:opacity-50"
            >
              {t("Try again", "حاول مجدداً")}
            </button>
          ) : null}
        </div>
      );
    }

    if (result?.kind === "rate_limited") {
      return (
        <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-rate-limited">
          {waitSentence(result.retryAfterMs)}
        </p>
      );
    }

    if (result?.kind === "empty") {
      return (
        <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-empty">
          {t(
            "Nothing to reflect on yet for this week.",
            "لا يوجد ما يمكن التأمّل فيه لهذا الأسبوع بعد.",
          )}
        </p>
      );
    }

    if (result?.kind === "success") {
      const r = result.reflection;
      return (
        <div className="space-y-3" data-testid="yourweek-reflection-result">
          <p className="text-sm text-foreground">{r.summary}</p>

          {r.progress.length > 0 ? (
            <div data-testid="yourweek-reflection-progress">
              <h3 className="text-xs font-semibold text-foreground">{t("Progress", "التقدّم")}</h3>
              <ul className="mt-1 list-disc space-y-1 ms-5 text-sm text-muted-foreground">
                {r.progress.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {r.friction.length > 0 ? (
            <div data-testid="yourweek-reflection-friction">
              <h3 className="text-xs font-semibold text-foreground">{t("Friction", "العوائق")}</h3>
              <ul className="mt-1 list-disc space-y-1 ms-5 text-sm text-muted-foreground">
                {r.friction.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {r.questionsForNextWeek.length > 0 ? (
            <div data-testid="yourweek-reflection-questions">
              <h3 className="text-xs font-semibold text-foreground">
                {t("Questions for next week", "أسئلة للأسبوع القادم")}
              </h3>
              <ul className="mt-1 list-disc space-y-1 ms-5 text-sm text-muted-foreground">
                {r.questionsForNextWeek.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.truncated ? (
            <p className="text-xs text-muted-foreground" data-testid="yourweek-reflection-truncated">
              {t(
                "Some weekly context was shortened for this reflection.",
                "اختُصر بعض سياق الأسبوع لأجل هذا التأمّل.",
              )}
            </p>
          ) : null}

          <p className="text-xs text-muted-foreground" data-testid="yourweek-reflection-framing">
            {t(
              "AI-generated. Review it critically. Based on your selected weekly context. Visible only to you.",
              "من إنتاج الذكاء الاصطناعي. راجعه بعين ناقدة. استناداً إلى سياق أسبوعك المحدّد. مرئي لك وحدك.",
            )}
          </p>
        </div>
      );
    }

    // Idle, and the fail-safe for any result variant this client does not know.
    // An unrecognised kind shows the idle body rather than a stale success.
    return (
      <p className="text-sm text-muted-foreground" data-testid="yourweek-reflection-idle">
        {t(
          "AI can help you notice progress, friction, and questions for next week.",
          "يمكن للذكاء الاصطناعي أن يساعدك على ملاحظة التقدّم والعوائق وأسئلة الأسبوع القادم.",
        )}
      </p>
    );
  };

  return (
    <div className="space-y-3">
      {body()}
      {disclosure}
      {action}
    </div>
  );
}
